import React, { useEffect, useMemo, useState } from 'react';
import { activityLogService } from '../../services/activityLogService';
import './SyntaxGuessCard.css';

const PLACEHOLDER = '___';

const getDefaultSyntaxCards = (topicName) => {
  const baseCards = [
    {
      id: 'syntax_default_print',
      prompt: 'Fill in the missing command to display a message.',
      snippet: '___("Hello, coder!")',
      options: ['print', 'say', 'display', 'speak'],
      correctOption: 0,
      timeLimit: 15,
      points: 15
    },
    {
      id: 'syntax_default_condition',
      prompt: 'Which keyword checks this comparison?',
      snippet: '___ age > 10:',
      options: ['when', 'if', 'loop', 'check'],
      correctOption: 1,
      timeLimit: 12,
      points: 10
    },
    {
      id: 'syntax_default_loop',
      prompt: 'Pick the word that repeats the block 5 times.',
      snippet: 'for i in range(5):\n    ___(i)',
      options: ['repeat', 'say', 'print', 'loop'],
      correctOption: 2,
      timeLimit: 18,
      points: 12
    }
  ];

  const lowerTopic = topicName?.toLowerCase() || '';
  if (lowerTopic.includes('function')) {
    baseCards.push({
      id: 'syntax_default_function',
      prompt: 'Choose the keyword used to start a function.',
      snippet: '___ greet(name):',
      options: ['func', 'define', 'def', 'function'],
      correctOption: 2,
      timeLimit: 15,
      points: 15
    });
  }

  return baseCards;
};

const SyntaxGuessCard = ({ cards, onPointsEarned, userId, courseId, topicName }) => {
  const defaultCards = useMemo(() => getDefaultSyntaxCards(topicName), [topicName]);
  const availableCards = useMemo(() => (
    cards && cards.length > 0 ? cards : defaultCards
  ), [cards, defaultCards]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [status, setStatus] = useState(null); // 'correct', 'wrong', 'timeout'
  const [timeLeft, setTimeLeft] = useState(availableCards[0]?.timeLimit || 20);

  useEffect(() => {
    setCurrentIndex(0);
  }, [availableCards.length]);

  const currentCard = availableCards[Math.min(currentIndex, Math.max(availableCards.length - 1, 0))];

  useEffect(() => {
    if (!currentCard) return;
    setSelectedOption(null);
    setStatus(null);
    setTimeLeft(currentCard.timeLimit || 20);
  }, [currentCard]);

  useEffect(() => {
    if (!currentCard || status) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setStatus('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentCard, status]);

  useEffect(() => {
    const logAttempt = async () => {
      if (!userId || !courseId || !currentCard || !status) return;

      try {
        await activityLogService.logActivity(
          userId,
          activityLogService.ACTIVITY_TYPES.SYNTAX_GUESS_ATTEMPT,
          {
            courseId,
            topicName,
            cardId: currentCard.id || currentIndex,
            selectedOption,
            correctOption: currentCard.correctOption ?? 0,
            status,
            timeRemaining: timeLeft
          }
        );
      } catch (error) {
        console.debug('Syntax guess logging failed', error);
      }
    };

    logAttempt();
  }, [status, currentCard, userId, courseId, topicName, selectedOption, timeLeft, currentIndex]);

  if (!currentCard) {
    return (
      <div className="syntax-guess-card">
        <p>No syntax cards available yet.</p>
      </div>
    );
  }

  const normalizedOptions = currentCard.options && currentCard.options.length > 0
    ? currentCard.options
    : ['Option 1', 'Option 2', 'Option 3', 'Option 4'];

  const handleGuess = (optionIdx) => {
    if (status) return;

    const correctIndex = currentCard.correctOption ?? 0;
    const isCorrect = optionIdx === Number(correctIndex);
    setSelectedOption(optionIdx);
    setStatus(isCorrect ? 'correct' : 'wrong');

    if (isCorrect) {
      const pointsEarned = Number(currentCard.points) || 10;
      onPointsEarned?.(pointsEarned);
    }
  };

  const handleNext = () => {
    if (currentIndex < availableCards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const placeholderToken = currentCard.placeholderToken || PLACEHOLDER;
  const snippetParts = currentCard.snippet
    ? currentCard.snippet.split(placeholderToken)
    : [''];

  const renderSnippet = () => (
    <code>
      {snippetParts.map((part, idx) => (
        <span key={`${currentCard.id || 'snippet'}-${idx}`}>
          {part}
          {idx < snippetParts.length - 1 && (
            <span
              className={`syntax-blank ${status === 'correct' ? 'correct' : ''} ${status && status !== 'correct' ? 'wrong' : ''}`.trim()}
            >
              {status && selectedOption !== null
                ? normalizedOptions[selectedOption]
                : placeholderToken}
            </span>
          )}
        </span>
      ))}
    </code>
  );

  const progressText = `${currentIndex + 1} / ${availableCards.length}`;
  const timerPercent = Math.max(0, Math.min(100, (timeLeft / (currentCard.timeLimit || 20)) * 100));

  return (
    <div className="syntax-guess-card">
      <div className="syntax-card-header">
        <div>
          <h4>⌛ Syntax Speed Round</h4>
          <p className="syntax-card-prompt">{currentCard.prompt}</p>
        </div>
        <div className="syntax-card-meta">
          <span className="syntax-progress">{progressText}</span>
          <span className="syntax-points">⭐ {currentCard.points || 10} pts</span>
        </div>
      </div>

      <div className="syntax-timer">
        <div className="timer-label">Time Left: {timeLeft}s</div>
        <div className="timer-bar">
          <div className="timer-fill" style={{ width: `${timerPercent}%` }} />
        </div>
      </div>

      <div className="syntax-code-block">
        <pre className="syntax-code">{renderSnippet()}</pre>
      </div>

      <div className="syntax-options">
        {normalizedOptions.map((option, idx) => (
          <button
            key={idx}
            className={`syntax-option ${selectedOption === idx ? 'selected' : ''} ${status && idx === (currentCard.correctOption ?? 0) ? 'correct' : ''}`}
            onClick={() => handleGuess(idx)}
            disabled={!!status}
          >
            {option}
          </button>
        ))}
      </div>

      {status && (
        <div className={`syntax-result ${status}`}>
          {status === 'correct' && '🎉 Great job! You nailed the syntax!'}
          {status === 'wrong' && '💡 Oops! Check the syntax and try again.'}
          {status === 'timeout' && '⏰ Time ran out! Keep practicing your speed.'}
        </div>
      )}

      {status && (
        <div className="syntax-actions">
          <button className="next-syntax-btn" onClick={handleNext}>
            {currentIndex < availableCards.length - 1 ? 'Next Challenge →' : 'Play Again 🔁'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SyntaxGuessCard;
